import { useCallback, useRef, useState } from 'react';

import { ZONE_COLORS } from '../constants/adminPageConstants';
import {
  MIN_POLYGON_POINTS,
  closePolygon,
  countUniquePoints,
  dedupeClosingPoints,
  resolveFramePoints,
} from '../utils/polygon';

export const useDrawingLogic = () => {
  const [zones, setZones] = useState([]);
  const [activeZoneIdx, setActiveZoneIdx] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingChanged, setDrawingChanged] = useState(false);

  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const drawZone = useCallback((ctx, points, colorIdx, isActive) => {
    if (!points || points.length < 1) return;

    const color = ZONE_COLORS[colorIdx % ZONE_COLORS.length];

    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i][0], points[i][1]);
    }
    if (points.length > 2) {
      ctx.closePath();
    }

    ctx.strokeStyle = color.stroke;
    ctx.lineWidth = isActive ? 3 : 2;
    ctx.stroke();

    if (points.length > 2) {
      ctx.fillStyle = color.fill;
      ctx.fill();
    }

    ctx.fillStyle = color.point;
    points.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point[0], point[1], isActive ? 5 : 4, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.fillStyle = color.stroke;
    ctx.font = 'bold 13px Arial';
    ctx.fillText(`Зона ${colorIdx + 1}`, points[0][0] + 6, points[0][1] - 6);
  }, []);

  const redrawCanvas = useCallback((currentZones, currentActive) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }

    (currentZones ?? []).forEach((zone, index) => {
      if (zone?.length) {
        drawZone(ctx, zone, index, index === (currentActive ?? 0));
      }
    });
  }, [drawZone]);

  const initializeDrawing = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!image.complete || image.naturalWidth === 0) return;

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    // Зона «весь кадр» приходит sentinel'ом с отрицательными координатами;
    // размер кадра известен только здесь, поэтому разворачиваем её сейчас и
    // фиксируем в состоянии — сохранение уйдёт уже абсолютными пикселями.
    const resolved = zones.map((zone) => resolveFramePoints(zone, canvas.width, canvas.height));
    if (JSON.stringify(resolved) !== JSON.stringify(zones)) setZones(resolved);
    redrawCanvas(resolved, activeZoneIdx);
  }, [activeZoneIdx, redrawCanvas, zones]);

  const handleCanvasClick = useCallback((event) => {
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    // FE-Z01: клик округляется и зажимается в границы канваса — координата не
    // должна улетать за пределы original-кадра из-за дробного округления вверх.
    const clamp = (value, max) => Math.min(Math.max(value, 0), max);
    const x = clamp(Math.round((event.clientX - rect.left) * scaleX), canvas.width);
    const y = clamp(Math.round((event.clientY - rect.top) * scaleY), canvas.height);

    setZones((prev) => {
      const next = prev.map((zone, index) => (
        index === activeZoneIdx ? [...zone, [x, y]] : [...zone]
      ));
      setDrawingChanged(true);
      redrawCanvas(next, activeZoneIdx);
      return next;
    });
  }, [activeZoneIdx, isDrawing, redrawCanvas]);

  const resetActiveZone = useCallback(() => {
    setZones((prev) => {
      const next = prev.map((zone, index) => (index === activeZoneIdx ? [] : [...zone]));
      setDrawingChanged(true);
      redrawCanvas(next, activeZoneIdx);
      return next;
    });
  }, [activeZoneIdx, redrawCanvas]);

  /**
   * FE-Z02: идемпотентно и без alert — повторный клик по «Замкнуть контур»
   * ничего не добавляет (closePolygon не трогает уже замкнутый контур), а
   * решение, что показать пользователю при нехватке точек, принимает
   * компонент по возвращённой причине, а не сам хук через alert().
   */
  const finishDrawing = useCallback(() => {
    const activeZone = zones[activeZoneIdx] ?? [];
    if (countUniquePoints(activeZone) < MIN_POLYGON_POINTS) {
      return { ok: false, reason: 'too_few_points' };
    }

    const closed = closePolygon(activeZone);
    setZones((prev) => {
      const next = prev.map((zone, index) => (index === activeZoneIdx ? closed : [...zone]));
      setDrawingChanged(true);
      redrawCanvas(next, activeZoneIdx);
      return next;
    });
    setIsDrawing(false);
    return { ok: true, reason: '' };
  }, [activeZoneIdx, redrawCanvas, zones]);

  const addZone = useCallback(() => {
    setZones((prev) => {
      const next = [...prev, []];
      const newIndex = next.length - 1;
      setActiveZoneIdx(newIndex);
      setIsDrawing(true);
      setDrawingChanged(true);
      redrawCanvas(next, newIndex);
      return next;
    });
  }, [redrawCanvas]);

  const deleteActiveZone = useCallback(() => {
    if (!zones.length) return;

    setZones((prev) => {
      const next = prev.filter((_, index) => index !== activeZoneIdx);
      const newIndex = Math.max(0, activeZoneIdx - 1);
      setActiveZoneIdx(newIndex);
      setIsDrawing(false);
      setDrawingChanged(true);
      redrawCanvas(next, newIndex);
      return next;
    });
  }, [activeZoneIdx, redrawCanvas, zones.length]);

  const selectZone = useCallback((index) => {
    setActiveZoneIdx(index);
    setIsDrawing(false);
    redrawCanvas(zones, index);
  }, [redrawCanvas, zones]);

  const toggleDrawing = useCallback(() => {
    if (!isDrawing) {
      if (zones.length === 0) {
        setZones([[]]);
        setActiveZoneIdx(0);
        redrawCanvas([[]], 0);
      }
      setIsDrawing(true);
      setDrawingChanged(true);
      return;
    }

    setIsDrawing(false);
  }, [isDrawing, redrawCanvas, zones.length]);

  const loadZones = useCallback((nextZones) => {
    // FE-Z04: дубли закрывающей точки не должны накапливаться при повторном
    // reload (например, после каждого сохранения зоны).
    const preparedZones = nextZones?.length ? nextZones.map(dedupeClosingPoints) : [];
    setZones(preparedZones);
    setActiveZoneIdx(0);
    setIsDrawing(false);
    setDrawingChanged(false);
  }, []);

  const resetDrawingState = useCallback(() => {
    setZones([]);
    setActiveZoneIdx(0);
    setIsDrawing(false);
    setDrawingChanged(false);
  }, []);

  return {
    zones,
    activeZoneIdx,
    isDrawing,
    drawingChanged,
    canvasRef,
    imageRef,
    initializeDrawing,
    handleCanvasClick,
    resetActiveZone,
    finishDrawing,
    addZone,
    deleteActiveZone,
    selectZone,
    toggleDrawing,
    loadZones,
    resetDrawingState,
    setDrawingChanged,
  };
};
